/* WeQuote Dev Board — Overview (renderOverview, faithful port). Needs-
 * Attention tiles are LINKED VIEWS — clicking one opens the Priority List with
 * that category filter pre-applied (one-visit, §45); the Completed donut is
 * period-filtered by dev; Working-on-now shows up to 2 in-design cards and the
 * top card per active dev; Top 8 Priorities; the Needs-Review list. Every card
 * opens the shared pop-up — resolving from here stays on the Overview (§44). */
import React from 'react'
import { useDevBoard } from '../DevBoardProvider'
import type { Feature } from '../core/types'
import { DEVCOLS, DL, PL, devColor } from '../core/constants'
import { timeLabel } from '../core/constants'
import { fmtDate, daysLeft, rankCls } from '../core/dates'
import { rankMap, inPipelineF, isDesignOverdue, isDevOverdue, isDesignStartSoon, checklistProgress } from '../core/logic'
import { reviewReqLabel } from '../core/review'
import * as OV from '../core/overview'
import { TitleWithBug } from './parts'
import { IcPencil, IcGear, IcClock } from '../icons'

const ChkCountLine: React.FC<{ feat: Feature }> = ({ feat }) => {
  const p = checklistProgress(feat)
  if (!p.total) return null
  const done = p.done === p.total
  return <div className={'ovx-mini-chk' + (done ? ' done' : '')}>{p.done}/{p.total} checkpoints{done ? ' ✓' : ''}</div>
}

export const OverviewView: React.FC = () => {
  const { cards, ovYear, ovMonth, setOvYear, setOvMonth, openFocusFilter, openCardModal, setView } = useDevBoard()
  const rmap = rankMap(cards)
  const byPo = (a: Feature, b: Feature) => a.po - b.po
  const ranked = cards.filter(inPipelineF).sort(byPo)

  const tiles = [
    { mode: 'design-overdue' as const, ic: <IcPencil size={14} />, cls: 'red', label: 'Design overdue', n: cards.filter(isDesignOverdue).length },
    { mode: 'dev-overdue' as const, ic: <IcGear size={14} />, cls: 'orange', label: 'Development overdue', n: cards.filter(isDevOverdue).length },
    { mode: 'design-soon' as const, ic: <IcClock size={14} />, cls: 'amber', label: 'Design due, dev starting soon', n: cards.filter(isDesignStartSoon).length },
  ]

  const completed = OV.completedInPeriod(cards, ovYear, ovMonth)
  const reqs = OV.requestsInPeriod(cards, ovYear, ovMonth)
  const byDev = OV.completedByDev(completed)
  const pie = OV.pieSlices(byDev)
  const legendKeys = Object.keys(byDev).sort((a, b) => byDev[b] - byDev[a])
  const years = OV.overviewYears(cards, new Date().getFullYear())

  const designAll = cards.filter((f) => f.status === 'design').sort(byPo)
  const MiniDesign: React.FC<{ f: Feature }> = ({ f }) => {
    const dl = daysLeft(f.designDeadline)
    return (
      <div className="ovx-mini" onClick={() => openCardModal(f.id)}>
        <div className="ovx-mini-t"><TitleWithBug feat={f} /></div>
        <div className="pbar"><div className="pbar-fill" style={{ width: (f.designProgress || 0) + '%' }} /></div>
        <div className="ovx-mini-row">
          <span className="ovx-mini-pct">{f.designProgress || 0}% designed</span>
          <span className="ovx-mini-when">{f.designDeadline ? <>{fmtDate(f.designDeadline)} · <span className={'dleft ' + dl.cls}>{dl.txt}</span></> : <span className="dleft grey">No design deadline</span>}</span>
        </div>
        <ChkCountLine feat={f} />
      </div>
    )
  }
  const MiniDev: React.FC<{ f: Feature }> = ({ f }) => {
    const dl = daysLeft(f.deadline)
    return (
      <div className="ovx-mini" onClick={() => openCardModal(f.id)}>
        <div className="ovx-mini-t"><TitleWithBug feat={f} /></div>
        <div className="pbar"><div className="pbar-fill" style={{ width: (f.progress || 0) + '%' }} /></div>
        <div className="ovx-mini-row">
          <span className="ovx-mini-pct">{f.progress || 0}% complete</span>
          <span className="ovx-mini-when">{f.deadline ? <>{fmtDate(f.deadline)} · <span className={'dleft ' + dl.cls}>{dl.txt}</span></> : <span className="dleft grey">No deadline set</span>}</span>
        </div>
        <ChkCountLine feat={f} />
      </div>
    )
  }

  const reviewItems = cards.filter((f) => f.status === 'needsreview')

  return (
    <div id="overviewView" style={{ padding: '16px 24px' }}>
      <div className="ovx">
        <div className="ovx-left">
          <div className="ovx-attention">
            <div className="ovc-hd"><h3>Needs Attention</h3><span className="ovc-sub">click a tile to open the cards</span></div>
            <div className="ovx-tiles">
              {tiles.map((t) => (
                <button key={t.mode} className="ovtile" onClick={() => openFocusFilter(t.mode)}>
                  <div className="ovtile-top"><span className={'ovtile-ic ' + t.cls}>{t.ic}</span><span className="ovtile-lbl">{t.label}</span><span className="ovtile-num">{t.n}</span></div>
                  <div className="ovtile-foot"><span className={'ovpill ' + t.cls}>{t.n} card{t.n === 1 ? '' : 's'}</span><span className="ovtile-view">View ›</span></div>
                </button>
              ))}
            </div>
          </div>
          <div className="ovx-flow">
            <div className="ovx-inprog ovc">
              <div className="ovc-hd"><h3>Working on now</h3><span className="ovc-sub">2 design · 1 per dev</span></div>
              <div className="ovc-body">
                <div className="ovx-grp">
                  <div className="ovx-grp-hd"><span>In design</span><span>{designAll.length}</span></div>
                  {designAll.length ? designAll.slice(0, 2).map((f) => <MiniDesign key={f.id} f={f} />) : <div className="ovx-col-empty">None</div>}
                </div>
                {DEVCOLS.map((d) => {
                  const mine = cards.filter((f) => f.status === d).sort(byPo)
                  if (!mine.length) return null
                  return (
                    <div key={d} className="ovx-grp">
                      <div className="ovx-grp-hd"><span>{DL[d]}</span><span>{mine.length}</span></div>
                      <MiniDev f={mine[0]} />
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="ovx-review ovc">
              <div className="ovc-hd"><div className="ovc-hd-l"><h3>Needs Review</h3><span className="ovpill grey">{reviewItems.length}</span></div><button className="ovx-btn" onClick={() => setView('review')}>View all</button></div>
              <div className="ovc-body">
                {reviewItems.length === 0 ? (
                  <div className="ovx-empty">Nothing to review right now.</div>
                ) : (
                  reviewItems.map((f) => (
                    <div key={f.id} className="ovx-rv" onClick={() => openCardModal(f.id)}>
                      <div className="ovx-rv-top"><span className={'badge priority-' + f.pri}>{PL[f.pri]}</span><span className="ovx-rv-t"><TitleWithBug feat={f} /></span></div>
                      <div className="ovx-rv-req">{reviewReqLabel(f)}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="ovx-right">
          <div className="ovx-completed ovc">
            <div className="ovc-hd">
              <h3>Completed</h3>
              <div className="ovc-tools">
                <select value={ovMonth === 'all' ? 'all' : String(ovMonth)} onChange={(e) => setOvMonth(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))}>
                  <option value="all">All months</option>
                  {OV.OV_MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
                </select>
                <select value={ovYear} onChange={(e) => setOvYear(parseInt(e.target.value, 10))}>
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <div className="ovx-ana-body">
              {pie.total ? (
                <svg viewBox="0 0 120 120" width={118} height={118} className="ovx-pie">
                  {pie.single ? <circle cx={60} cy={60} r={54} fill={pie.color} /> : pie.slices.map((sl, i) => <path key={i} d={sl.d} fill={sl.color} />)}
                  <circle cx={60} cy={60} r={32} fill="#fff" />
                  <text x={60} y={59} textAnchor="middle" fontSize={22} fontWeight={700} fill="#111827">{pie.total}</text>
                  <text x={60} y={75} textAnchor="middle" fontSize={9} fill="#9ca3af">done</text>
                </svg>
              ) : (
                <div className="ovx-empty">No features completed in this period.</div>
              )}
              {legendKeys.length > 0 && (
                <div className="ovx-legend">
                  {legendKeys.map((k) => (
                    <div key={k} className="ovx-leg"><span className="ovx-sw" style={{ background: devColor(k) }} />{k === 'unassigned' ? 'Unassigned' : DL[k] || k}<span className="ovx-leg-n">{byDev[k]}</span></div>
                  ))}
                </div>
              )}
            </div>
            <div className="ovx-ana-foot">
              <div><div className="ovx-stat-n">{completed.length}</div><div className="ovx-stat-l">Completed</div></div>
              <div><div className="ovx-stat-n">{reqs.length}</div><div className="ovx-stat-l">Requests added</div></div>
            </div>
          </div>
          <div className="ovx-priorities ovc">
            <div className="ovc-hd"><h3>Top 8 Priorities</h3><span className="ovc-sub">tap to open</span></div>
            <div className="ovc-body">
              {ranked.length === 0 ? (
                <div className="ovx-empty">Nothing prioritised yet.</div>
              ) : (
                ranked.slice(0, 8).map((f) => (
                  <div key={f.id} className="ovx-rank" onClick={() => openCardModal(f.id)}>
                    <span className={'pnum-badge ' + rankCls(rmap[f.id])}>{rmap[f.id]}</span>
                    <span className="ovx-rank-t"><TitleWithBug feat={f} /></span>
                    <span className="ovx-rank-meta">{f.time ? timeLabel(f.time) : 'TBD'} · {f.dev && DL[f.dev] ? DL[f.dev] : 'Unassigned'}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
